package diana.soleil.design.iraniancanadienbazzar.feature.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** The signed-in user's activity notifications (chat messages + review replies), newest first. */
data class NotificationsUiState(
    val loading: Boolean = true,
    val notifications: List<AppNotification> = emptyList(),
    /** Guest (or signed-out) — the route sends them to sign-in instead. */
    val signInRequired: Boolean = false,
    val error: Boolean = false,
) {
    /** Drives the "Mark all read" action — hidden when everything is already read. */
    val hasUnread: Boolean get() = notifications.any { !it.read }
}

class NotificationsViewModel(
    private val repository: NotificationRepository,
) : ViewModel() {

    // Bumped by retry() after a load failure — flatMapLatest resubscribes the stream fresh.
    private val retryTrigger = MutableStateFlow(0)

    @OptIn(ExperimentalCoroutinesApi::class)
    val state: StateFlow<NotificationsUiState> =
        retryTrigger
            .flatMapLatest {
                repository.observeNotifications()
                    .map { result ->
                        when (result) {
                            is DataResult.Success -> NotificationsUiState(loading = false, notifications = result.data)
                            is DataResult.Failure -> NotificationsUiState(
                                loading = false,
                                signInRequired = result.error is AppError.Unauthorized,
                                error = result.error !is AppError.Unauthorized,
                            )
                        }
                    }
                    .catch { emit(NotificationsUiState(loading = false, error = true)) }
                    // Back to the spinner while the retried subscription warms up.
                    .onStart { emit(NotificationsUiState()) }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), NotificationsUiState())

    /** Re-subscribes the notification stream after a failure (the error state's retry action). */
    fun retry() {
        retryTrigger.value += 1
    }

    /** Marks a notification read on open. Messages clear the whole conversation; others clear one. */
    fun markRead(notification: AppNotification) {
        viewModelScope.launch {
            val conversationId = notification.conversationId
            if (conversationId != null) {
                repository.markConversationRead(conversationId)
            } else {
                repository.markRead(notification.id)
            }
        }
    }

    /** Clears the unread dot on every notification at once. */
    fun markAllRead() {
        viewModelScope.launch { repository.markAllRead() }
    }
}
